import React, { useState } from 'react';
import { X, Copy, Check, Download } from 'lucide-react';
import {
  exportSectionToBase64,
  downloadSectionAsFile,
  countAllComponents,
} from '../../../utils/sectionExportImport';

/**
 * Modal for exporting a section as base64-encoded data.
 * Allows copying to clipboard or downloading as a .section file.
 */
export default function ExportSectionModal({ isOpen, onClose, section }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !section) {
    return null;
  }

  // Generate base64 encoding of the section
  let base64Data = '';
  try {
    base64Data = exportSectionToBase64(section);
  } catch (error) {
    console.error('Failed to export section:', error);
  }

  // Get section info for display
  const sectionType = section.section_type || 'Unknown';
  const componentCount = countAllComponents(section);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Copy base64 to clipboard with success feedback
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(base64Data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Download section as .section file
  const handleDownload = () => {
    try {
      downloadSectionAsFile(section);
    } catch (error) {
      console.error('Failed to download section:', error);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Export Section</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          {/* Section Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>
                <span className="font-medium text-gray-700">Type:</span> {sectionType}
              </span>
              {componentCount !== null && (
                <span>
                  <span className="font-medium text-gray-700">Components:</span> {componentCount}
                </span>
              )}
            </div>
          </div>

          {/* Base64 Textarea */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Encoded Section Data
            </label>
            <textarea
              readOnly
              value={base64Data}
              className="w-full h-40 p-3 text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Character Count */}
          <div className="text-right text-xs text-gray-500">
            {base64Data.length.toLocaleString()} characters
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCopyToClipboard}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy to Clipboard</span>
              </>
            )}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Download File</span>
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
